import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ColumnState, ColGroupDef } from 'ag-grid-community';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { TabType } from './Store_KSK1081_R01';
import { getTabGridOptions } from './components/ColumnDefinitions';
import useKSK1081_R01Store from './Store_KSK1081_R01';
import { aggridHeaderClass, tableTabFormHeightOption } from 'src/components/common/common';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { generateRowId } from './utils/rowIdUtils';
import { 
    calculateItemSubtotals, 
    calculateWarehouseSubtotals, 
    calculateLogicCenterSubtotals,
    mergeDataWithSubtotals,
    isSubtotalRow,
    isItemSubtotalRow,
    isWarehouseSubtotalRow,
    isLogicCenterSubtotalRow
} from './utils/subtotalUtils';
import { defaultKSK1081_R01TotalDto, KSK1081_R01Dto } from './Dto_KSK1081_R01';

interface StockGridBaseProps {
    gridRef: React.RefObject<AgGridReact<any> | null>;
    tabType: TabType;
    data: any[];
    columns: ColDef[];
    onTabChange: (tab: TabType) => Promise<void>;
}

export const Table_KSK1081_R01: React.FC<StockGridBaseProps> = ({
    gridRef,
    tabType,
    data,
    columns,
    onTabChange
}) => {
    const {
        activeTab,
        tabColumnStates,
        setTabColumnState,
        tabSelectedIds,
        setTabSelectedId,
        showSubtotals,
        tabSubtotalData,
        setTabSubtotalData,
        tabGridData,
        setTabGridData,
    } = useKSK1081_R01Store();

    // AG Grid 충돌 방지를 위한 키
    const [gridKey, setGridKey] = useState(0);
    
    // 탭 변경 시 AG Grid를 완전히 새로 생성
    useEffect(() => {
        setGridKey(prev => prev + 1);
    }, [tabType]);

    // 소계 데이터 계산 (메모이제이션 적용)
    const subtotals = useMemo(() => {
        if (!data || data.length === 0 || tabType === 'item' || tabType === 'total') {
            return [];
        }
        
        switch (tabType) {
            case 'warehouse':
                return calculateWarehouseSubtotals(data);
            case 'logiccenter':
                return calculateLogicCenterSubtotals(data);
            default:
                return [];
        }
    }, [data, tabType]);

    // 그리드 데이터 생성 (메모이제이션 적용)
    const gridData = useMemo(() => {
        if (!data || data.length === 0) {
            return [];
        }
        
        return mergeDataWithSubtotals(data, subtotals, showSubtotals, tabType);
    }, [data, subtotals, showSubtotals, tabType]);

    // 소계 데이터와 그리드 데이터를 상태에 저장
    useEffect(() => {
        setTabSubtotalData(tabType, subtotals);
        setTabGridData(tabType, gridData);
    }, [subtotals, gridData, tabType, setTabSubtotalData, setTabGridData]);
    
    // 로컬 스토리지 키
    const localStorageKey = useMemo(() => {
        return `KSK1081_R01_${tabType}_columns`;
    }, [tabType]);

    // 이전 컬럼 정의를 저장하기 위한 ref (useMemo보다 먼저 선언)
    const prevColumnDefs = useRef<ColDef[]>([]);

    // 탭 버튼 구성
    const tabs = useMemo(() => ([
        { id: 'total' as const, label: '전체 재고' },
        { id: 'item' as const, label: '품목별 재고' },
        { id: 'warehouse' as const, label: '창고별 재고' },
        { id: 'logiccenter' as const, label: '물류센터별 재고' },
    ]), []);

    // 탭 변경 핸들러 (UI 이벤트만 처리)
    const handleTabClick = useCallback(async (tab: TabType) => {
        if (tab === activeTab) return; // 같은 탭 클릭 시 무시
        
        // 부모 컴포넌트의 onTabChange 호출
        await onTabChange(tab);
    }, [onTabChange, activeTab]);

    // 행 ID 생성 함수 - 소계 행 고려
    const getRowId = useCallback((params: any) => {
        const data = params.data;
        
        // 소계 행인 경우 - 접두사 추가로 중복 방지
        if (isSubtotalRow(data)) {
            return `subtotal_${data.groupKey || data.itemCd}`;
        }
        
        // 상세 행인 경우 - 공통 함수 사용
        return generateRowId(data, tabType);
    }, [tabType]);

    // 데이터 객체만 받아 동일 규칙으로 ID 생성 - 소계 행 고려
    const makeRowId = useCallback((data: any) => {
        // 소계 행인 경우 - 접두사 추가로 중복 방지
        if (isSubtotalRow(data)) {
            return `subtotal_${data.groupKey || data.itemCd}`;
        }
        
        // 상세 행인 경우 - 공통 함수 사용
        return generateRowId(data, tabType);
    }, [tabType]);


    // 컬럼 정의: 행 인덱스 컬럼 + 전달된 컬럼들
    const columnDefs = useMemo(() => {
        const indexColumn = getDefaultRowIndexColumnConfig();
        const baseColumnDefs = [...indexColumn, ...columns];
        
        // 저장된 컬럼 상태가 빈 배열이면 null로 처리하여 기본 컬럼을 사용
        const savedState = tabColumnStates[tabType];
        const effectiveState = Array.isArray(savedState) && savedState.length === 0 ? null : savedState;
        
        // AG Grid 충돌 방지: 실제 변경이 있을 때만 업데이트
        const mergedColumnDefs = GridCustomColumnDefs({
            baseColumnDefs,
            columnState: effectiveState || null,
            headerNames: {}, // 빈 객체 (기본 헤더명 사용)
            fallbackHeaderClass: aggridHeaderClass
        });

        // 병합 후 헤더 클래스/메뉴 억제를 재귀적으로 강제 주입
        const normalizeHeaderClass = (defs: ColDef[]): ColDef[] =>
            defs.map((def: any) => {
                const mergedHeaderClass = [aggridHeaderClass, def.headerClass].filter(Boolean).join(' ');
                const children = Array.isArray(def.children) && def.children.length > 0
                    ? normalizeHeaderClass(def.children)
                    : undefined;
                return { ...def, headerClass: mergedHeaderClass, suppressMenu: true, children } as ColDef;
            });

        const newColumnDefs = normalizeHeaderClass(mergedColumnDefs);

        // 합계(핀드 바텀) 라벨이 표시되는 컬럼들에 대해 하단 행만 중앙 정렬 적용
        const bottomLabelCols = new Set(['itemCd', 'warehouseNm', 'logicCenterNm']);
        const withPinnedCenterAlign = newColumnDefs.map((def: any) => {
            if (!def?.colId || !bottomLabelCols.has(def.colId)) return def as ColDef;
            const prevCellStyle = def.cellStyle;
            const cellStyle = (params: any) => {
                if (params?.node?.rowPinned === 'bottom') {
                    return { ...(typeof prevCellStyle === 'function' ? prevCellStyle(params) : prevCellStyle), textAlign: 'center' };
                }
                return typeof prevCellStyle === 'function' ? prevCellStyle(params) : prevCellStyle;
            };
            return { ...def, cellStyle } as ColDef;
        });
        
        // 문제의 원인이었던 수동 최적화 코드를 완전히 제거합니다.
        // React와 useMemo가 의존성 배열을 통해 렌더링을 올바르게 관리합니다.
        return withPinnedCenterAlign;
    }, [columns, tabColumnStates, tabType]);

    // 이 테이블 전용 기본 컬럼 옵션(헤더 아이콘 숨김)
    const localDefaultColDef = useMemo(() => ({
        ...defaultColDef,
        suppressMenu: true,
        headerClass: aggridHeaderClass,
    }), []);
    
    // 행 선택 하이라이트 규칙 (단일 선택)
    const rowClassRules = useMemo(() => {
        const selectedId = tabSelectedIds[tabType];
        return {
            selected: (params: any) => {
                if (params.node.rowPinned === 'bottom') return false;
                if (!selectedId) return false;
                const id = makeRowId(params.data);
                return id === selectedId;
            },
            hover: (params: any) => {
                if (params.node.rowPinned === 'bottom') return false;
                if (!params.data) return false;
                const id = makeRowId(params.data);
                return id !== selectedId;
            },
            'subtotal-row': (params: any) => {
                return isSubtotalRow(params.data);
            },
        } as Record<string, (p: any) => boolean>;
    }, [tabSelectedIds, tabType, makeRowId]);

    // 행 클릭으로 단일 선택/해제 (소계 행 제외)
    const handleRowClicked = useCallback((e: any) => {
        if (!e?.data || e.node?.rowPinned === 'bottom') return;
        
        // 소계 행 클릭시 아무 동작하지 않음
        if (isSubtotalRow(e.data)) {
            return;
        }
        
        const id = makeRowId(e.data);
        if (!id) return;
        
        // 현재 선택된 ID와 비교
        const currentSelectedId = tabSelectedIds[tabType];
        
        // 같은 행 클릭 시 선택 해제, 다른 행 클릭 시 선택
        if (currentSelectedId === id) {
            setTabSelectedId(tabType, undefined);
        } else {
            setTabSelectedId(tabType, id);
        }
    }, [setTabSelectedId, tabType, makeRowId, tabSelectedIds]);

    // 합계(핀드 바텀 로우) 계산
    const pinnedBottomRowData = useMemo(() => {
        if (!Array.isArray(data) || data.length === 0) return [] as any[];
        
        const number = (v: any) => Number(v) || 0;
        const dataLength = data?.length || 0;
        
        switch (tabType) {
            case 'item': {
                const total = {
                    itemCd: `${dataLength.toLocaleString()}건`,
                    stockQty: data.reduce((s, r: any) => s + number(r.stockQty), 0),
                    reservQty: data.reduce((s, r: any) => s + number(r.reservQty), 0),
                    availableQty: data.reduce((s, r: any) => s + number(r.stockQty), 0), // 가용재고는 현재고를 사용
                } as any;
                return [total];
            }
            case 'warehouse': {
                const total = {
                    warehouseNm: `${dataLength.toLocaleString()}건`,
                    stockQty: data.reduce((s, r: any) => s + number(r.stockQty), 0),
                    reservQty: data.reduce((s, r: any) => s + number(r.reservQty), 0),
                    availableQty: data.reduce((s, r: any) => s + number(r.stockQty), 0), // 가용재고는 현재고를 사용
                } as any;
                return [total];
            }
            case 'logiccenter': {
                const total = {
                    logicCenterNm: `${dataLength.toLocaleString()}건`,
                    stockQty: data.reduce((s, r: any) => s + number(r.stockQty), 0),
                    reservQty: data.reduce((s, r: any) => s + number(r.reservQty), 0),
                    availableQty: data.reduce((s, r: any) => s + number(r.stockQty), 0), // 가용재고는 현재고를 사용
                } as any;
                return [total];
            }
            case 'total': {
                const total = {
                    logicCenterNm: `${dataLength.toLocaleString()}건`,
                    stockQty: data.reduce((s, r: any) => s + number(r.stockQty), 0),
                    reservQty: data.reduce((s, r: any) => s + number(r.reservQty), 0),
                    availableQty: data.reduce((s, r: any) => s + number(r.stockQty), 0), // 가용재고는 현재고를 사용
                } as any;
                return [total];
            }
            default:
                return [] as any[];
        }
    }, [data, tabType]);

    return (
        <div className="w-full h-full flex flex-col">
            {/* 소계 행 스타일 */}
            <style>{`
                .ag-row.subtotal-row {
                    background-color: #f0f8ff !important;
                    color: #2c5aa0 !important;
                    font-weight: bold !important;
                }
                .ag-row.subtotal-row:hover {
                    background-color: #f0f8ff !important;
                }
            `}</style>
            
            {/* 탭 버튼 섹션 - GridContextWrapper 위에 위치 */}
            <div
                className="flex items-end mb-[0.1px]  bg-white h-[25px] "
                role="tablist"
                aria-label="재고 조회 탭"
            >
                {tabs.map((tab) => {
                    const isActive = activeTab === (tab.id as TabType);
                    return (
                        <button
                            key={tab.id}
                            onClick={() => handleTabClick(tab.id as TabType)}
                            role="tab"
                            aria-selected={isActive}
                            className={`px-3 font-medium transition-colors border border-gray-300 border-b-0 items-center justify-center	
								w-[160px]
								${tableTabFormHeightOption}
                                ${isActive
                                    ? 'bg-white text-black-700 -mb-px shadow-sm'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                            `}
                        >
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            {/* 그리드 섹션 */}
            <div className="flex-1">
                <GridContextWrapper<KSK1081_R01Dto>
                    gridRef={gridRef}
                    paddedGridData={tabGridData[tabType] || []}
                    initSelected={() => {}} // 선택 기능 없음
					defaultRowData={defaultKSK1081_R01TotalDto}
                    baseColumnDefs={columnDefs}
                    setColumnState={(state: ColumnState[] | null) => setTabColumnState(tabType, state || [])}
                    localStorageKey={localStorageKey}
                >
                    <Grid_Excel<KSK1081_R01Dto>
                        key={gridKey}
                        ref={gridRef as any}
                        rowData={tabGridData[tabType] || []}
                        columnDefs={columnDefs}
                        defaultColDef={localDefaultColDef}
                        getRowId={getRowId}
                        rowClassRules={rowClassRules}
                        pinnedBottomRowData={pinnedBottomRowData}
                        onRowClicked={handleRowClicked}
                        columnStateKey={localStorageKey}
                        setColumnState={(state: ColumnState[]) => setTabColumnState(tabType, state || [])}
                        isRowSelectable={(node) => !!node.data && !isSubtotalRow(node.data)}
                        {...getTabGridOptions()}
                    />
                </GridContextWrapper>
            </div>
        </div>
    );
};
